import { useMemo, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useCarousel } from './hooks/useCarousel';
import { useGetParams } from './hooks/useGetParams';
import { useVote } from './hooks/useVote';

import CTA from './components/CTA/CTA';
import Quiz from './components/Quiz/Quiz';
import AdvtFullSize from './components/AdvtFullSize/AdvtFullSize';
import { Winners } from './components/Winners/Winners';
import MessagesList from './components/MessagesList/MessagesList';

import shark from './assets/images/shark.png';

import posterDefault from './assets/images/poster.png';
import bgDefaultVideo from './assets/video/bgVideo.mp4';

import logo from './assets/images/logo.png';
import delo from './assets/images/delo.png';
import ncsp from './assets/images/ncsp.png';
import logo2 from './assets/images/logoRnd.webp';

function App() {
  const { voteState, currentState } = useVote();
  const { winners, showedMessages, winnerMessage, speed, setSpeed } =
    useCarousel();

  const {
    user,
    authorFontSize,
    msgFontSize,
    phoneNumberFontSize,
    bgVideo,
    bgPoster,
    speedSlow,
    speedFast,

    phoneNumber,
    socials,
    title,
    qrCode1,
    qrCode2,
    winnerFontSize,
    titreHeight,
  } = useGetParams();

  useEffect(() => {
    setTimeout(() => {
      if (currentState === 'S6' && speed !== speedSlow) {
        setSpeed(speedSlow);
      } else if (currentState === 'S7' && speed !== speedFast) {
        setSpeed(speedFast);
      }
    }, [500]);
  }, [currentState]);

  const advt_info = useMemo(
    () => ({
      qrCode1: qrCode1,
      qrCode2: qrCode2,
      label: title,
      number: phoneNumber,
      socials: socials?.split(','),
    }),
    [phoneNumber, socials, title, qrCode1, qrCode2]
  );

  return (
    <div
      className="app-wrapper"
      style={{
        '--height': `${Number(titreHeight) / 9.6}rem`,
        '--speed': `${speed}ms`,
      }}
    >
      <AnimatePresence>
        {['S1', 'S2', 'S3'].includes(currentState) && (
          <motion.div
            key="logoContainer1"
            style={{ width: '100%' }}
            initial={{ opacity: 0, position: 'fixed' }}
            animate={{ opacity: 1, position: 'fixed' }}
            exit={{ y: '-200vh', opacity: 0, position: 'fixed' }}
            transition={{ duration: 1, delay: 0 }}
          >
            <div className="logoContainer1">
              <img src={shark} alt="shark" className="sharkImage" />

              <img src={logo} alt="logo" className="logo1" />
              <img src={delo} alt="delo" className="delo" />
              <img src={ncsp} alt="ncsp" className="ncsp" />
            </div>
          </motion.div>
        )}
        {['S6', 'S7', 'S8'].includes(currentState) && (
          <motion.div
            key="logoContainer2"
            style={{ width: '100%' }}
            initial={{ opacity: 0, position: 'fixed' }}
            animate={{ opacity: 1, position: 'fixed' }}
            exit={{ y: '-200vh', position: 'fixed', opacity: 0 }}
            transition={{ duration: 1, delay: 0 }}
          >
            <div className="logoContainer2">
              <img src={logo2} alt="logo" className="logo2" />
              <img src={delo} alt="delo" className="delo" />
              <img src={ncsp} alt="ncsp" className="ncsp" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {currentState === 'S1' ? (
          <motion.div
            key="cta"
            initial={{ y: 0, opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '170%' }}
            transition={{ duration: 0.5 }}
            className="motionBlock"
          >
            <CTA title={voteState.title} description={voteState.description} />
          </motion.div>
        ) : null}

        {['S2'].includes(currentState) && (
          <motion.div
            key="bigAdvt2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '-150%' }}
            transition={{ duration: 0.5 }}
            className="motionBlock"
          >
            <AdvtFullSize advtInfo={advt_info} currentState={currentState} />
          </motion.div>
        )}

        {['S3'].includes(currentState) && (
          <motion.div
            key={'quiz' + voteState?.id}
            className="motionBlock"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '170%' }}
            transition={{ duration: 0.5 }}
          >
            <Quiz variants={voteState?.vars} />
          </motion.div>
        )}
        {currentState === 'S4' && (
          <motion.div
            key="winners"
            className="motionBlock"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '-150%' }}
            transition={{ duration: 0.5 }}
          >
            <Winners winnerMessages={winners || []} />
          </motion.div>
        )}

        {['S6', 'S7', 'S8', 'S9'].includes(currentState) && (
          <motion.div
            key="msgs"
            className="motionBlock"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '170%' }}
            transition={{ duration: 0.5 }}
          >
            <MessagesList
              winId={winnerMessage?.message.id}
              messages={showedMessages}
              authorFontSize={authorFontSize}
              msgFontSize={msgFontSize}
              phoneNumberFontSize={phoneNumberFontSize}
              currentState={currentState}
              speed={speed || 0}
              user={user}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {bgVideo && (
        <video
          autoPlay="autoplay"
          muted="muted"
          loop
          poster={bgVideo === '1' ? posterDefault : bgPoster}
          id="bgVideo"
        >
          <source
            src={bgVideo === '1' ? bgDefaultVideo : bgVideo}
            type="video/mp4"
          ></source>
        </video>
      )}
      <div id="img-loader" />
    </div>
  );
}

export default App;
